import config from '../config';
import {
  SCAPI_SHORT_CODE,
  SCAPI_ORG_ID,
  SCAPI_PRIVATE_CLIENT_ID,
  SCAPI_PRIVATE_CLIENT_SECRET
} from '../env/server';

/**
 * Describes valid product mapping strategy options. The option selected will be
 * used in `SFCCServerCategoryServiceAdapter`.
 */
export type ProductMappingStrategyName =
  | 'imageUrl'
  | 'masterAndVariants'
  | 'colorVariants'
  | 'mastersOnly'
  | 'slicingGroup'
  | 'showAllHits';

/** Configuration for Salesforce Commerce Cloud integrations. */
const sfcc = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      /**
       * Options for the B2C Commerce API, a.k.a. Salesforce Commerce API (SCAPI).
       * @see https://developer.salesforce.com/docs/commerce/commerce-api/overview
       */
      scapi: {
        /**
         * Base URL to use for SCAPI requests.
         * @see https://developer.salesforce.com/docs/commerce/commerce-api/guide/base-url.html#base-url-pattern
         */
        baseURL: `https://${SCAPI_SHORT_CODE}.api.commercecloud.salesforce.com`,

        /**
         * Short string that identifies a B2C Commerce instance.
         * @see https://developer.salesforce.com/docs/commerce/commerce-api/guide/base-url.html#organization-id
         */
        organizationID: SCAPI_ORG_ID,

        /**
         * Options for the Shopper Login and API Access Service (SLAS), which
         * enables secure access to the Shopper APIs.
         *
         * @see https://developer.salesforce.com/docs/commerce/commerce-api/guide/slas.html
         */
        slas: {
          /**
           * The ID for the private client Next's BFF will use.
           * @see https://developer.salesforce.com/docs/commerce/commerce-api/guide/slas.html#slas-clients
           */
          privateClientID: SCAPI_PRIVATE_CLIENT_ID,

          /**
           * The secret for the private client Next's BFF will use.
           * @see https://developer.salesforce.com/docs/commerce/commerce-api/guide/slas.html#slas-clients
           */
          privateClientSecret: SCAPI_PRIVATE_CLIENT_SECRET,

          /**
           * The redirect URI registered with the SLAS client in Account
           * Manager. Required for registered user authentication (PKCE flow).
           *
           * This value must match one of the redirect URIs configured in SLAS
           * Admin. For server-side PKCE flows where no actual redirect happens,
           * any registered URI works (the flow completes without browser
           * redirect).
           *
           * @see https://developer.salesforce.com/docs/commerce/commerce-api/guide/slas-private-client.html
           */
          redirectUri: 'http://localhost:3000/callback',

          /**
           * Token expiration buffer in seconds.
           *
           * Tokens are considered expired and refreshed this many seconds
           * before their actual expiration time to prevent race conditions
           * where a token expires between validation and use.
           *
           * Current value: 15 minutes. SLAS token refresh can fail transiently
           * (409 rate limits, network errors). A large buffer gives the
           * middleware multiple request cycles to retry the refresh while the
           * token is still valid, avoiding user-visible errors. If the refresh
           * fails within the buffer window, the handler falls through with the
           * existing token instead of clearing the session.
           */
          tokenExpirationBufferSeconds: 60 * 15, // 15 minutes

          /**
           * Token storage configuration for SLAS authentication.
           *
           * Controls how authentication tokens are stored in cookies and
           * server sessions.
           *
           * @see AuthService
           */
          tokenStorage: {
            /**
             * Cookie name prefix for auth tokens. Cookie names are formed as:
             * `{prefix}access_token`, `{prefix}refresh_token`, etc.
             */
            cookiePrefix: 'auth_',

            /**
             * Session storage key for tokens backup. Used for backwards
             * compatibility with AWS session storage.
             */
            sessionKey: 'auth_tokens',

            /**
             * Cookie expiration time in seconds.
             * Default: 7 days (604800 seconds) for long-lived sessions.
             */
            cookieMaxAgeSeconds: 7 * 24 * 60 * 60 // 7 days in seconds
          }
        },

        /** Settings related to SCAPI product processing. */
        products: {
          /**
           * Controls whether SCAPI product requests (base and variant) should
           * be batched using `RequestBatcher`. If true, requests made within
           * the `batchingWindow` are grouped together.
           *
           * @see `RequestBatcher` in `@/libs/utils/RequestBatcher.ts`
           */
          batchingEnabled: true,

          /**
           * The time window in milliseconds during which SCAPI product requests
           * are collected before being sent as a batch. Only applicable if
           * `batchingEnabled` is true.
           *
           * @see `RequestBatcher` in `@/libs/utils/RequestBatcher.ts`
           */
          batchingWindow: 50,

          /**
           * The maximum number of items that can be batched together in a single
           * request. Only applicable if `batchingEnabled` is true. A value of 0
           * disables the maximum batch size limit.
           *
           * @see `RequestBatcher` in `@/libs/utils/RequestBatcher.ts`
           */
          maxPerBatch: 0,

          /**
           * Determines the product mapping strategy to use when adapting SCAPI
           * product hits to DECA SKUs.
           *
           * @see `SFCCServerCategoryServiceAdapter`
           */
          productMappingStrategy: 'showAllHits' as ProductMappingStrategyName,

          /**
           * Time-to-live (TTL) in minutes for cached variant products.
           *
           * Variant products fetched during PLP processing are cached to improve
           * performance when users navigate to PDP pages. This setting controls
           * how long these products remain in the cache.
           *
           * Set to 0 to disable caching.
           */
          variantProductCacheTTL: 30
        },

        /**
         * Settings related to SCAPI navigation/category processing.
         *
         * It will store relevant configuration for `SFCCNavigationService` and how
         * to process the navigational data returned by it.
         */
        navigation: {
          /**
           * When true, category URLs returned by SCAPI will use the
           * `c_alternativeUrl` custom attribute if available, instead of the
           * usual `href` property.
           */
          useAlternativeUrls: false
        },

        /**
         * Pricebook configuration for mapping pricebook IDs to their purposes.
         *
         * These IDs are brand and locale-specific and determine which price
         * lists are used for retail and sale pricing.
         *
         * Configure these values per brand/locale in the site-specific
         * overrides below.
         */
        pricebooks: {
          /**
           * The pricebook ID for regular retail prices.
           *
           * This represents the original price before any discounts.
           * When null, pricebook-based pricing is disabled.
           */
          retailPricebookId: null as string | null,

          /**
           * The pricebook ID for sale prices.
           *
           * When present and lower than retail, creates a sale price with
           * strikethrough display. When null, sale pricing is not available.
           */
          salePricebookId: null as string | null
        }
      }
    }
  },

  // Brand and locale-specific configurations
  'TEVA-US': {
    default: {
      scapi: {
        navigation: {
          /**
           * For Teva US, use the `c_alternativeUrl` custom attribute for
           * category URLs if available.
           */
          useAlternativeUrls: true
        },
        /**
         * Teva US-specific pricebook configuration. These IDs map to the
         * "Teva_US Retail" and "TEVA-US Sale" pricebooks in SFCC.
         */
        pricebooks: {
          retailPricebookId: '6087', // Teva_US Retail pricebook
          salePricebookId: '6123' // TEVA-US Sale pricebook
        }
      }
    }
  }

  // Add other brand configurations as needed:
});

export default sfcc;
